import { Modal, Button, Form, Col, Row } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../config';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';

function AddProductModal({ show, onHide, onProductAdded }) {
    // Add new state for submission status
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({
        productName: '',
        productCode: '',
        unitId: '',
        groupId: '',
        alias: '',
        sku: '',
        gst: '0',
        cess: '0',
        bufferStock: '',
        category: 'Row Material',
    });

    const [units, setUnits] = useState([]);
    const [groups, setGroups] = useState([]);
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);

    const categoryOptions = [
        { value: 'Row Material', label: 'Row Material' },
        { value: 'Finished Good', label: 'Finished Good' },
        { value: 'Scrap', label: 'Scrap' }
    ];

    const gstOptions = [
        { value: 0, label: '0' },
        { value: 5, label: '5%' },
        { value: 8, label: '8%' },
        { value: 12, label: '12%' },
        { value: 18, label: '18%' }
    ];
    const cessOptions = [
        { value: 0, label: '0' },
        { value: 2, label: '2%' },
    ];

    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const res = await axios.get(`${config.API_BASE_URL}/units`);
                setUnits(res.data);
            } catch (err) {
                console.error("Failed to load units", err);
            }
        };
        const fetchGroups = async () => {
            try {
                const res = await axios.get(`${config.API_BASE_URL}/groups`);
                setGroups(res.data);
            } catch (err) {
                console.error("Failed to load groups", err);
            }
        };
        fetchUnits();
        fetchGroups();
    }, []);

    const handleChange = e => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateGroup = async (inputValue) => {
        setIsCreatingGroup(true);
        try {
            const newGroup = {
                groupName: inputValue,
                groupCode: inputValue.substring(0, 3).toUpperCase(), // Simple code generation
            };

            const response = await axios.post(`${config.API_BASE_URL}/groups/new`, newGroup);
            const createdGroup = response.data.group;
            
            // Update groups list
            setGroups(prev => [...prev, createdGroup]);
            
            // Select the newly created group
            setForm(prev => ({ ...prev, groupId: createdGroup._id }));
            
            toast.success('New group created successfully');
            return createdGroup;
        } catch (error) {
            toast.error('Failed to create new group');
            console.error('Error creating group:', error);
            return null;
        } finally {
            setIsCreatingGroup(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Remove gst and cess from required fields check
        if (!form.unitId || !form.category || !form.alias || !form.sku) {
            toast.error('Please fill all required fields.');
            return;
        }

        setIsSubmitting(true); // Disable button while submitting

        try {
            const response = await axios.post(`${config.API_BASE_URL}/products/new`, {
                ...form,
                gst: form.gst || 0,  // Default to 0 if not selected
                cess: form.cess || 0  // Default to 0 if not selected
            });
            
            await onProductAdded(response.data); // Wait for parent refresh to complete
            onHide();
            // toast.success('Product added successfully!');
        } catch (err) {
            console.error('Failed to add product:', err);
            toast.error('Failed to add product!');
        } finally {
            setIsSubmitting(false); // Re-enable button after submission
        }
    };

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            size="lg" 
            fullscreen="lg-down"
            backdrop={isCreatingGroup || isSubmitting ? 'static' : true}
            keyboard={!isCreatingGroup && !isSubmitting}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Product</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Row className="gy-3">
                            <Col md={6}>
                                <Form.Label>Category</Form.Label>
                                <Select
                                    options={categoryOptions}
                                    value={categoryOptions.find(opt => opt.value === form.category)}
                                    onChange={selected => setForm(prev => ({ ...prev, category: selected?.value || '' }))}
                                    placeholder="Select category"
                                    isClearable
                                />
                                {!form.category && <div className="text-danger mt-1">Required</div>}
                            </Col>
                            <Col md={6}>
                                <Form.Label>Product Name</Form.Label>
                                <Form.Control name="productName" value={form.productName} onChange={handleChange} required />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Alias</Form.Label>
                                <Form.Control name="alias" value={form.alias} onChange={handleChange} required />
                            </Col>
                            <Col md={6}>
                                <Form.Label>HSN Code</Form.Label>
                                <Form.Control name="productCode" value={form.productCode} onChange={handleChange} required />
                            </Col>

                            <Col md={6}>
                                <Form.Label>SKU</Form.Label>
                                <Form.Control name="sku" value={form.sku} onChange={handleChange} required />
                            </Col>

                            <Col md={6}>
                                <Form.Label>GST (%)</Form.Label>
                                <Select
                                    options={gstOptions}
                                    value={gstOptions.find(opt => opt.value === Number(form.gst))}
                                    onChange={selected => setForm(prev => ({ ...prev, gst: selected?.value || '' }))}
                                    placeholder="Select GST"
                                />
                                {/* {!form.gst && <div className="text-danger mt-1">Required</div>} */}
                            </Col>
                            <Col md={6}>
                                <Form.Label>CESS (%)</Form.Label>
                                <Select
                                    options={cessOptions}
                                    value={cessOptions.find(opt => opt.value === Number(form.cess))}
                                    onChange={selected => setForm(prev => ({ ...prev, cess: selected?.value || '' }))}
                                    placeholder="Select CESS"
                                />
                                {/* {!form.cess && <div className="text-danger mt-1">Required</div>} */}
                            </Col>

                            <Col md={6}>
                                <Form.Label>Unit</Form.Label>
                                <Select
                                    options={units.map(unit => ({
                                        value: unit._id,
                                        label: `${unit.unitName} (${unit.unitCode})`
                                    }))}
                                    value={
                                        units.find(u => u._id === form.unitId)
                                            ? {
                                                value: form.unitId,
                                                label: `${units.find(u => u._id === form.unitId).unitName} (${units.find(u => u._id === form.unitId).unitCode})`
                                            }
                                            : null
                                    }
                                    onChange={selected => setForm(prev => ({ ...prev, unitId: selected?.value || '' }))}
                                    placeholder="Select unit"
                                    required
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Group</Form.Label>
                                <CreatableSelect
                                    isClearable
                                    isDisabled={isCreatingGroup}
                                    isLoading={isCreatingGroup}
                                    options={groups.map(group => ({
                                        value: group._id,
                                        label: `${group.groupName} (${group.groupCode})`
                                    }))}
                                    value={
                                        groups.find(g => g._id === form.groupId)
                                            ? {
                                                value: form.groupId,
                                                label: `${groups.find(g => g._id === form.groupId).groupName} (${groups.find(g => g._id === form.groupId).groupCode})`
                                            }
                                            : null
                                    }
                                    onChange={selected => setForm(prev => ({ ...prev, groupId: selected?.value || '' }))}
                                    onCreateOption={handleCreateGroup}
                                    placeholder="Select or create group..."
                                    formatCreateLabel={(inputValue) => `Create group "${inputValue}"`}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Buffer Stock</Form.Label>
                                <Form.Control 
                                    type="number" 
                                    name="bufferStock" 
                                    value={form.bufferStock} 
                                    onChange={handleChange} 
                                    min="0"
                                    step="0.01"
                                    placeholder='0'
                                />
                            </Col>
                        </Row>
                    </Form.Group>

                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={onHide}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default AddProductModal;
